<template>
  <v-dialog :value="value" max-width="900px" :fullscreen="isMobile" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center flex-wrap">
        <v-icon left small>mdi-chart-line</v-icon>
        Tendencia — {{ contrato }}
        <span class="body-2 font-weight-regular grey--text ml-2">
          {{ periodoLabel }}
        </span>
        <v-spacer />
        <span v-if="points.length" class="body-2 font-weight-regular grey--text mr-2">
          <v-icon x-small color="orange">mdi-circle</v-icon> Forecast &nbsp;
          <v-icon x-small color="primary">mdi-circle</v-icon> Real
        </span>
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <div v-if="loading" class="d-flex justify-center align-center py-10">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <!-- El backend responde 200 con lista vacía cuando el contrato no tiene
             días capturados: es un caso esperado, no un fallo. Por eso el vacío
             y el error se muestran distinto. -->
        <v-alert v-else-if="error" dense outlined type="error" class="mb-0">
          {{ error }}
        </v-alert>

        <div v-else-if="points.length === 0" class="text-center grey--text py-10">
          Sin datos diarios de MRR para este contrato en {{ periodoLabel }}
        </div>

        <div v-else>
          <div style="position: relative; height: 300px; width: 100%;">
            <line-chart :chart-data="chartData" :options="chartOptions" />
          </div>
          <p class="caption grey--text text--darken-1 mt-3 mb-0">
            Solo se grafican los días con captura diaria de MRR; los días sin dato no se rellenan.
          </p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { Line } from 'vue-chartjs';
import MrrDailyService from '../services/mrr-daily.service';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) {
          this.$data._chart.destroy();
        }
        this.renderChart(newData, this.options);
      },
    },
  },
};

export default {
  name: 'ContractMrrTrendDialog',
  components: { LineChart },
  props: {
    value: { type: Boolean, default: false },
    contrato: { type: String, default: '' },
    year: { type: [String, Number], default: null },
    month: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      error: null,
      points: [],
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    periodoLabel() {
      const m = Number(this.month);
      if (!this.year || !m) return '';
      return `${MESES[m - 1]} ${this.year}`;
    },
    chartData() {
      return {
        labels: this.points.map((p) => Number(p.date.slice(8, 10))),
        datasets: [
          {
            label: 'MRR (USD)',
            data: this.points.map((p) => p.mrrUSD),
            borderColor: '#1976D2',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            pointBackgroundColor: this.points.map((p) => (p.estimado ? '#FB8C00' : '#1976D2')),
            pointRadius: 4,
            pointHoverRadius: 6,
            borderWidth: 2,
            fill: true,
            tension: 0.25,
          },
        ],
      };
    },
    // Con pocos puntos (o uno solo) el autoescalado de Chart.js deja la curva
    // pegada al borde y repite etiquetas casi idénticas. Mismo colchón
    // proporcional que usa MRR Daily.
    yAxisRange() {
      const values = this.points.map((p) => p.mrrUSD);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const padding = Math.max((max - min) * 0.15, max * 0.02, 50);
      return {
        min: Math.max(0, Math.floor(min - padding)),
        max: Math.ceil(max + padding),
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, scaleLabel: { display: true, labelString: 'Día' } }],
          yAxes: [
            {
              ticks: {
                ...this.yAxisRange,
                maxTicksLimit: 6,
                callback: (v) => `US$${this.formatNumber(v)}`,
              },
              gridLines: { color: 'rgba(0,0,0,0.05)' },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (item) => {
              const p = this.points[item.index];
              const base = `US$${this.formatNumber(p.mrrUSD)}`;
              return p.estimado ? `${base} (forecast)` : base;
            },
          },
        },
      };
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatNumber(n) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n) || 0);
    },
    async load() {
      if (!this.contrato) return;
      this.loading = true;
      this.error = null;
      this.points = [];
      const res = await MrrDailyService.getContractSeries(
        this.contrato,
        this.year,
        this.month
      );
      if (res.status === 'ok') {
        this.points = res.data;
      } else {
        this.error = res.message;
      }
      this.loading = false;
    },
  },
};
</script>
